import React, { useState } from 'react';
import { Calendar, Play, Layers, CheckCircle, Clock, AlertCircle, XCircle, FileText } from 'lucide-react';
import { getFatigueColor, getFatigueLabel, formatLastUsed } from '../../utils/fatigueCalculator';

// Status configuration for visual indicators
const STATUS_CONFIG = {
    draft: {
        icon: FileText,
        color: 'var(--color-text-tertiary)',
        bg: 'var(--color-text-tertiary)',
        label: 'Draft'
    },
    scheduled: {
        icon: Clock,
        color: 'var(--color-primary)',
        bg: 'var(--color-primary)',
        label: 'Scheduled'
    },
    published: {
        icon: CheckCircle,
        color: 'var(--success-500)',
        bg: 'var(--success-500)',
        label: 'Published'
    },
    failed: {
        icon: AlertCircle,
        color: 'var(--error-500)',
        bg: 'var(--error-500)',
        label: 'Failed'
    },
    cancelled: {
        icon: XCircle,
        color: 'var(--color-text-tertiary)',
        bg: 'var(--color-text-tertiary)',
        label: 'Cancelled'
    }
};

/**
 * CreativeCard Component
 * A draggable card displaying a creative with fatigue indicators
 *
 * @param {Object} props
 * @param {Object} props.creative - The creative object
 * @param {Object} [props.usageData] - Usage statistics {usageCount, lastUsedDate, fatigueLevel}
 * @param {boolean} [props.compact=false] - Use compact layout for calendar cells
 * @param {boolean} [props.draggable=true] - Whether the card can be dragged
 * @param {function} [props.onDragStart] - Callback when drag starts
 * @param {function} [props.onDragEnd] - Callback when drag ends
 * @param {function} [props.onClick] - Callback when card is clicked
 * @param {Date} [props.scheduledDate] - If scheduled, the date it's scheduled for
 * @param {Array} [props.platforms] - Platforms this is scheduled for
 * @param {string} [props.status] - Post status: 'scheduled', 'published', 'failed', 'cancelled'
 * @param {string} [props.publishedAt] - ISO timestamp when the post was published
 * @param {string} [props.lastError] - Error message if post failed
 * @param {number} [props.publishAttempts] - Number of publish attempts
 */
const CreativeCard = ({
    creative,
    usageData = {},
    compact = false,
    draggable = true,
    onDragStart,
    onDragEnd,
    onClick,
    scheduledDate,
    platforms = [],
    status,
    publishedAt,
    lastError,
    publishAttempts
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [imageError, setImageError] = useState(false);

    const { usageCount = 0, lastUsedDate = null, fatigueLevel = 'fresh' } = usageData;
    const fatigueColor = getFatigueColor(fatigueLevel);
    const fatigueLabel = getFatigueLabel(fatigueLevel);

    const handleDragStart = (e) => {
        if (!draggable) {
            e.preventDefault();
            return;
        }

        setIsDragging(true);

        // Set drag data
        const dragData = {
            id: creative.id,
            title: creative.title,
            type: 'creative',
            creativeType: creative.type, // 'carousel', 'image', etc.
            url: creative.url || creative.imageUrl || creative.videoUrl,
            isVideo: creative.isVideo,
            isCarousel: isCarousel,
            carouselCardCount: carouselCardCount
        };

        e.dataTransfer.setData('application/json', JSON.stringify(dragData));
        e.dataTransfer.setData('application/type', 'creative');
        e.dataTransfer.effectAllowed = 'move';

        // Create drag image
        if (e.dataTransfer.setDragImage) {
            const dragElement = e.currentTarget.cloneNode(true);
            dragElement.style.opacity = '0.8';
            dragElement.style.position = 'absolute';
            dragElement.style.top = '-9999px';
            document.body.appendChild(dragElement);
            e.dataTransfer.setDragImage(dragElement, 50, 50);
            setTimeout(() => document.body.removeChild(dragElement), 0);
        }

        if (onDragStart) {
            onDragStart(creative, e);
        }
    };

    const handleDragEnd = (e) => {
        setIsDragging(false);
        if (onDragEnd) {
            onDragEnd(creative, e);
        }
    };

    const handleClick = (e) => {
        if (onClick) {
            onClick(creative, e);
        }
    };

    const thumbnailUrl = creative.url || creative.imageUrl || creative.videoUrl;
    const isVideo = creative.isVideo || !!creative.videoUrl;
    const isCarousel = creative.type === 'carousel';
    const carouselCardCount = isCarousel && creative.carouselImages?.length || 0;

    // Compact card for calendar cells
    if (compact) {
        return (
            <div
                draggable={draggable}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onClick={handleClick}
                style={{
                    width: '100%',
                    aspectRatio: '1/1',
                    borderRadius: 'var(--radius-md)',
                    overflow: 'hidden',
                    cursor: draggable ? 'grab' : 'pointer',
                    opacity: isDragging ? 0.5 : 1,
                    position: 'relative',
                    background: 'var(--color-bg-tertiary)',
                    border: '1px solid var(--color-border-light)',
                    transition: 'all var(--transition-fast)'
                }}
                title={creative.title}
            >
                {thumbnailUrl && !imageError ? (
                    <img
                        src={thumbnailUrl}
                        alt={creative.title}
                        onError={() => setImageError(true)}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                ) : (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-tertiary)',
                        fontSize: 'var(--font-size-lg)'
                    }}>
                        {(creative.title || '?').charAt(0).toUpperCase()}
                    </div>
                )}

                {isVideo && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Play size={12} color="white" fill="white" />
                    </div>
                )}

                {isCarousel && (
                    <div style={{
                        position: 'absolute',
                        top: '2px',
                        right: '2px',
                        background: 'rgba(0,0,0,0.7)',
                        borderRadius: '4px',
                        padding: '2px 4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '2px'
                    }}>
                        <Layers size={10} color="white" />
                        <span style={{ fontSize: '9px', color: 'white', fontWeight: 600 }}>
                            {carouselCardCount}
                        </span>
                    </div>
                )}

                {/* Status indicator overlay for compact cards */}
                {status && STATUS_CONFIG[status] && (
                    <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '2px',
                        background: STATUS_CONFIG[status].bg,
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}
                    title={status === 'failed' && lastError
                        ? `Failed: ${lastError}`
                        : STATUS_CONFIG[status].label}
                    >
                        {React.createElement(STATUS_CONFIG[status].icon, {
                            size: 10,
                            color: 'white'
                        })}
                    </div>
                )}
            </div>
        );
    }

    // Full card for dock
    return (
        <div
            draggable={draggable}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onClick={handleClick}
            className="card"
            style={{
                padding: 'var(--space-3)',
                cursor: draggable ? 'grab' : 'pointer',
                opacity: isDragging ? 0.5 : 1,
                transition: 'all var(--transition-fast)',
                borderLeft: `3px solid ${fatigueColor}`
            }}
        >
            {/* Thumbnail */}
            <div style={{
                width: '100%',
                aspectRatio: '16/9',
                borderRadius: 'var(--radius-md)',
                overflow: 'hidden',
                marginBottom: 'var(--space-2)',
                background: 'var(--color-bg-tertiary)',
                position: 'relative'
            }}>
                {thumbnailUrl && !imageError ? (
                    <img
                        src={thumbnailUrl}
                        alt={creative.title}
                        onError={() => setImageError(true)}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                ) : (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--color-text-tertiary)',
                        fontSize: 'var(--font-size-2xl)'
                    }}>
                        {(creative.title || '?').charAt(0).toUpperCase()}
                    </div>
                )}

                {isVideo && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        background: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Play size={18} color="white" fill="white" />
                    </div>
                )}

                {isCarousel && (
                    <div style={{
                        position: 'absolute',
                        top: 'var(--space-2)',
                        right: 'var(--space-2)',
                        background: 'rgba(0,0,0,0.7)',
                        borderRadius: 'var(--radius-sm)',
                        padding: '4px 8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <Layers size={12} color="white" />
                        <span style={{ fontSize: '11px', color: 'white', fontWeight: 600 }}>
                            {carouselCardCount} cards
                        </span>
                    </div>
                )}
            </div>

            {/* Title */}
            <h4 style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--space-2)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}>
                {creative.title || 'Untitled'}
            </h4>

            {/* Fatigue indicator */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-2)'
            }}>
                <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    background: fatigueColor,
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: 600
                }}>
                    {usageCount === 0 ? 'New' : `Used ${usageCount}x`}
                    {usageCount > 0 && ` (${fatigueLabel})`}
                </span>
            </div>

            {/* Last used */}
            {usageCount > 0 && (
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)'
                }}>
                    <Calendar size={12} />
                    Last: {formatLastUsed(lastUsedDate)}
                </div>
            )}

            {/* Status indicator for full cards */}
            {status && STATUS_CONFIG[status] && (
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: STATUS_CONFIG[status].color,
                    marginTop: 'var(--space-1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
                        {React.createElement(STATUS_CONFIG[status].icon, { size: 12 })}
                        {STATUS_CONFIG[status].label}
                        {status === 'published' && publishedAt && (
                            <span style={{ color: 'var(--color-text-tertiary)' }}>
                                • {new Date(publishedAt).toLocaleString()}
                            </span>
                        )}
                        {status === 'failed' && publishAttempts > 0 && (
                            <span style={{ color: 'var(--color-text-tertiary)' }}>
                                • {publishAttempts} attempt{publishAttempts !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    {status === 'failed' && lastError && (
                        <div style={{
                            fontSize: '10px',
                            color: 'var(--error-600)',
                            background: 'var(--error-100)',
                            padding: '2px 6px',
                            borderRadius: 'var(--radius-sm)',
                            marginTop: '2px',
                            wordBreak: 'break-word'
                        }}>
                            {lastError.length > 80 ? `${lastError.substring(0, 80)}...` : lastError}
                        </div>
                    )}
                </div>
            )}

            {/* Scheduled date if applicable */}
            {scheduledDate && !status && (
                <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-primary)',
                    marginTop: 'var(--space-1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)'
                }}>
                    <Calendar size={12} />
                    Scheduled: {new Date(scheduledDate).toLocaleDateString()}
                </div>
            )}

            {/* Platform badges */}
            {platforms && platforms.length > 0 && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-1)',
                    flexWrap: 'wrap',
                    marginTop: 'var(--space-2)'
                }}>
                    {platforms.slice(0, 3).map(platform => (
                        <span
                            key={platform}
                            className="badge badge-info"
                            style={{ fontSize: '10px' }}
                        >
                            {platform}
                        </span>
                    ))}
                    {platforms.length > 3 && (
                        <span style={{
                            fontSize: '10px',
                            color: 'var(--color-text-tertiary)'
                        }}>
                            +{platforms.length - 3}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

export default CreativeCard;
